<template>
    <span v-if="status" class="icon-status" :class="statusClass" :style="statusStyle">
        <i :class="iconClass"></i>
    </span>
</template>

<script>
export default {
    name: 'StatusIcon',

    props: {
        status: {
            type: String,
            default: null,
        },
        // When provided, this context-aware normalized status will be used for icon display
        // Values: 'none', 'approved', 'denied', 'change_requested'
        normalizedStatus: {
            type: String,
            default: null,
        },
    },

    computed: {
        // Extract the raw status name from the full class name
        rawStatusName() {
            if (!this.status) return '';
            const parts = this.status.split('\\');
            return (parts[parts.length - 1] || '').toLowerCase();
        },

        // Use normalized status if provided, otherwise fall back to raw status name
        effectiveStatus() {
            return this.normalizedStatus || this.rawStatusName;
        },

        statusClass() {
            // Map for normalized statuses (context-aware)
            const normalizedMap = {
                none: 'icon-pending',
                approved: 'icon-thumbs-up',
                denied: 'icon-denied',
                change_requested: 'icon-running',
            };

            // Map for raw statuses (backwards compatibility)
            const rawMap = {
                admin_change_requested: 'icon-running',
                team_change_requested: 'icon-running',
                admin_approved: 'icon-thumbs-up',
                team_approved: 'icon-thumbs-up',
                complete: 'icon-check',
                fulfilled: 'icon-ribbon',
                unfulfilled: 'icon-denied',
                paid: 'icon-paid',
                admin_denied: 'icon-denied',
                team_denied: 'icon-denied',
                expired: 'icon-expired',
            };

            // Use normalized map if normalizedStatus prop is provided
            if (this.normalizedStatus) {
                return normalizedMap[this.effectiveStatus] || 'icon-pending';
            }

            return rawMap[this.effectiveStatus] || normalizedMap[this.effectiveStatus] || 'icon-pending';
        },

        statusStyle() {
            // Map for normalized statuses (context-aware)
            const normalizedMap = {
                none: { background: '#fff', border: '1px solid #e8eef2', color: '#6c757d' },
                approved: { background: '#fff', border: '1px solid #35647e', color: '#35647E' },
                denied: { background: '#d11f2c', border: '1px solid #d11f2c', color: '#fff' },
                change_requested: { background: '#fff', border: '1px solid #109DEA', color: '#109DEA' },
            };

            // Map for raw statuses (backwards compatibility)
            const rawMap = {
                admin_change_requested: { background: '#fff', border: '1px solid #109DEA', color: '#109DEA' },
                team_change_requested: { background: '#fff', border: '1px solid #109DEA', color: '#109DEA' },
                admin_approved: { background: '#fff', border: '1px solid #35647e', color: '#35647E' },
                team_approved: { background: '#fff', border: '1px solid #35647e', color: '#35647E' },
                complete: { background: '#1BA66A', border: '1px solid #1BA66A', color: '#fff' },
                fulfilled: { background: '#fff', border: '1px solid #35647e', color: '#35647e' },
                unfulfilled: { background: '#fff', border: '1px solid #e8eef2', color: '#d11f2c' },
                paid: { background: '#fff', border: '1px solid #35647e', color: '#35647E' },
                admin_denied: { background: '#d11f2c', border: '1px solid #d11f2c', color: '#fff' },
                team_denied: { background: '#d11f2c', border: '1px solid #d11f2c', color: '#fff' },
                expired: { background: '#d11f2c', border: '1px solid #d11f2c', color: '#fff' },
            };

            // Use normalized map if normalizedStatus prop is provided
            if (this.normalizedStatus) {
                return normalizedMap[this.effectiveStatus] || normalizedMap.none;
            }

            return rawMap[this.effectiveStatus] || normalizedMap[this.effectiveStatus] || normalizedMap.none;
        },

        iconClass() {
            // Map for normalized statuses (context-aware)
            const normalizedMap = {
                none: 'fa fa-clock',
                approved: 'fal fa-thumbs-up',
                denied: 'fa fa-times',
                change_requested: 'fal fa-chevron-double-right',
            };

            // Map for raw statuses (backwards compatibility)
            const rawMap = {
                admin_change_requested: 'fal fa-chevron-double-right',
                team_change_requested: 'fal fa-chevron-double-right',
                admin_approved: 'fal fa-thumbs-up',
                team_approved: 'fal fa-thumbs-up',
                complete: 'fal fa-check',
                fulfilled: 'fal fa-award',
                unfulfilled: 'fa fa-times',
                paid: 'fa fa-dollar-sign',
                admin_denied: 'fa fa-times',
                team_denied: 'fa fa-times',
                expired: 'fa fa-clock',
            };

            // Use normalized map if normalizedStatus prop is provided
            if (this.normalizedStatus) {
                return normalizedMap[this.effectiveStatus] || 'fa fa-clock';
            }

            return rawMap[this.effectiveStatus] || normalizedMap[this.effectiveStatus] || 'fa fa-clock';
        },
    },
};
</script>

<style scoped>
.icon-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 0.7rem;
}
</style>
